import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ChangeDetectionStrategy,
  ComponentRef,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, switchMap, of, finalize, shareReplay, map } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { DprHseRegistrationsGridComponent } from '@dpr-details/components';
import { DprDetailsPageBaseComponent } from '@dpr-details/models/component';
import { DprDetailsService } from '@dpr-details/services';
import { getDefaultSubMenuItems } from '@dpr-details/utils';
import { GridSync } from '@grid/models';
import { DprDetailsSync, DprHse } from '@shared/models/dpr';
import { ScreenSizeService, ToastNotificationService } from '@shared/services';
import { DprHseDetailGridData } from '../../models/grid';
import { DprHseGridSync } from '@dpr-details/models/sync';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import { DprHseGridService } from '@dpr-details/services/grid';
import { getStaticMessage } from '@shared/utils';
import { StaticMessage } from '@shared/enums';
import { DprAttachmentsGridData } from '@shared/models/grid';
import { DprAttachmentService } from '@shared/services/dpr';
import { NavigationMenuItem } from '@shared/models';

@UntilDestroy()
@Component({
  selector: 'app-dpr-hse-page',
  templateUrl: './dpr-hse-page.component.html',
  styleUrls: ['./dpr-hse-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprHsePageComponent
  extends DprDetailsPageBaseComponent<DprHseDetailGridData>
  implements OnInit
{
  labeling = {
    gridEntityName: $localize`:@@dprDetails.dprHsePage.txtHseRegistrations:Hse Registrations`,
  };

  StaticMessage = StaticMessage;

  preselectedItem: string | number;
  navigationSelectItems: NavigationMenuItem[] = getDefaultSubMenuItems();

  constructor(
    private dprHseGridService: DprHseGridService,
    private dprDetailsService: DprDetailsService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    protected activatedRoute: ActivatedRoute,
    protected dprAttachmentService: DprAttachmentService,
    private toastNotificationService: ToastNotificationService,
    protected screenSizeService: ScreenSizeService
  ) {
    super(activatedRoute, dprAttachmentService);
  }

  get dprHse(): DprHse {
    return this.dprDetails.dprHSE;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.preselectedItem =
      this.activatedRoute?.firstChild?.snapshot?.url[0]?.path;
  }

  save(
    _?,
    isAutoSync = false,
    isAutoSyncFromAdminChange = false
  ): Observable<boolean> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;

    if (this.dprHseGridService.shouldAutoFill(this.registrationsGridData)) {
      this.autoFillEmptyItems();
    }

    // store activated component changes
    this.storeComponentChanges(this.activatedComponent);

    const validateChanges$ =
      isAutoSync || isAutoSyncFromAdminChange
        ? of(true)
        : this.validateChanges(this.attachmentsChanges);

    const saveChanges$ = validateChanges$.pipe(
      untilDestroyed(this),
      switchMap((valid) => {
        if (!valid) {
          return of(false);
        }

        return this.saveChanges(
          this.registrationsChanges,
          this.attachmentsChanges,
          this.comment,
          isAutoSyncFromAdminChange
        );
      }),
      finalize(() => {
        this.isSaving = false;
      }),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  onSelectionChange(currentSelection: string | number): void {
    this.preselectedItem = currentSelection;
  }

  //#region Protected methods

  protected isRegistrationComponent(component: ComponentRef<any>): boolean {
    return component instanceof DprHseRegistrationsGridComponent;
  }

  protected setupData(): void {
    this.registrationsGridData = this.dprHseGridService.buildGridData(
      this.selectedSubproject.data?.hseList,
      this.dprHse.dprhseDetails,
      this.dprHse.id
    );
    this.attachmentsGridData = this.dprAttachmentService.mapToGridData(
      this.dprHse?.dprhseAttachments
    );
    this.comment = this.dprHse.comment;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected getData(): void {}

  protected getOfflineRegistrationsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<DprHseDetailGridData> {
    const offlineChanges = dprDetailsOfflineChanges?.dprHSE?.syncDprHseDetails;
    if (!offlineChanges) {
      return null;
    }

    const offlineGridData = this.dprHseGridService.buildGridData(
      this.selectedSubproject.data?.hseList,
      [
        ...(offlineChanges.addedList ?? []),
        ...(offlineChanges.updatedList ?? []),
      ],
      this.dprHse.id
    );

    return this.getOfflineGridDataChanges(offlineGridData, offlineChanges);
  }

  protected getOfflineAttachmentsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<DprAttachmentsGridData> {
    const offlineChanges =
      dprDetailsOfflineChanges?.dprHSE?.syncDprHseAttachments;
    return this.getOfflineAttachmentGridDataChanges(offlineChanges);
  }

  //#endregion

  //#region Private methods

  private validateChanges(
    attachmentsChanges: GridSync<DprAttachmentsGridData>
  ): Observable<boolean> {
    if (!this.dprAttachmentService.validateChanges(attachmentsChanges)) {
      return of(false);
    }

    return of(true);
  }

  private saveChanges(
    registrationsChanges: GridSync<DprHseDetailGridData>,
    attachmentsChanges: GridSync<DprAttachmentsGridData>,
    comment: string,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprHse> {
    const syncDprHseDetails =
      this.dprHseGridService.mapToGridSync(registrationsChanges);

    const syncData: DprHseGridSync =
      this.dprDetailsSyncModelBuilderService.forDprHse(
        this.selectedSubproject.id,
        this.dprDetails.id,
        this.dprHse.id,
        syncDprHseDetails,
        attachmentsChanges,
        comment
      );

    const saveChanges$ = this.dprDetailsService
      .syncDprHseRegistrations(syncData, isAutoSyncFromAdminChange)
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.onSaveSuccess(result);
      },
      error: (error: HttpErrorResponse) => {
        this.saveErrorHandlerService.onSaveError(
          this.customTabNames.hseTabName,
          this.selectedSubproject.id,
          error
        );
      },
    });

    return saveChanges$;
  }

  private autoFillEmptyItems(): void {
    if (!this.registrationsGridData?.length) {
      return;
    }

    this.toastNotificationService.info(
      getStaticMessage(
        StaticMessage.AutoFillText,
        this.customTabNames.hseTabName
      ),
      false,
      getStaticMessage(
        StaticMessage.AutoFillTitle,
        this.customTabNames.hseTabName
      )
    );

    this.registrationsGridData.forEach((x) => {
      if (x.today == null) {
        this.registrationsGridApi.editCell(x, 'today', 0);
      }
    });

    this.registrationsChanges = this.registrationsGridApi.getChanges();
  }

  private onSaveSuccess(result: DprHse): void {
    this.refreshData();
    this.offlineFacade.setDprHse(result, this.dprDetails.id);
  }

  //#endregion
}
